import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { config } from '@shared/lib/config';
import { EMPLOYEES_TAG, TEMPLATE_TAG, EMPLOYEE_STATUS_TAG, COMPANY_TAG, EMPLOYEE_TAG, CUSTOMERS_TAG } from './tags';
import { RootState } from '@shared/model';
import { AuthService } from '@shared/lib/services';
import { CredentialsDto, logout, setCredentials } from '@entities/user';

export const baseQuery = fetchBaseQuery({
  baseUrl: config.API_ENDPOINT,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const { accessToken } = state.user;

    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }

    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const reqBody = AuthService.getRefreshTokenReqDto();
    const credentials = await baseQuery(
      {
        url: '/users/auth/refresh-token',
        method: 'POST',
        body: reqBody,
      },
      api,
      extraOptions
    );

    if (credentials.data) {
      const data = credentials.data as CredentialsDto;
      const { accessToken, refreshToken, userId } = data;

      AuthService.setCredentialsToLocalStorage(data);

      api.dispatch(
        setCredentials({
          userId: userId,
          accessToken: accessToken,
          refreshToken: refreshToken,
        })
      );

      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logout());
    }
  }

  return result;
};

export const baseApi = createApi({
  tagTypes: [EMPLOYEES_TAG, EMPLOYEE_STATUS_TAG, TEMPLATE_TAG, COMPANY_TAG, EMPLOYEE_TAG, CUSTOMERS_TAG],
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
});
